import { useState, useCallback } from "react";
import { ID } from "appwrite";
import { storage, BUCKET_ID } from "../appwrite";

/**
 * Custom hook for handling file operations with Appwrite Storage
 * @returns {Object} File operation methods and state
 */
export const useFile = () => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  /**
   * Upload a file to Appwrite Storage
   * @param {File} file - The file to upload
   * @param {string} [fileId] - Optional custom file ID (default: auto-generated)
   * @param {Function} [onProgress] - Optional progress callback
   * @param {number} [timeout=60000] - Upload timeout in milliseconds (default: 60 seconds)
   * @returns {Promise<Object>} Upload response with fileId and fileUrl
   */
  const uploadFile = useCallback(async (file, fileId = ID.unique(), onProgress = null, timeout = 60000) => {
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Validate file
      if (!file) {
        throw new Error("No file provided");
      }

      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("Upload timeout: File upload took too long. Please try again later."));
        }, timeout);
      });

      // Create upload promise
      const uploadPromise = storage.createFile(
        BUCKET_ID,
        fileId,
        file,
        // Optional: Add permissions if needed
        // ['read("any")']
      );

      // Race between upload and timeout
      const response = await Promise.race([uploadPromise, timeoutPromise]);

      // Get file URL
      const fileUrl = storage.getFileView(BUCKET_ID, response.$id);
      
      setProgress(100);
      
      return {
        success: true,
        fileId: response.$id,
        fileUrl: fileUrl,
        response,
      };
    } catch (err) {
      console.error("Error uploading file:", err);
      setError(err.message || "Failed to upload file");
      return {
        success: false,
        error: err.message || "Failed to upload file",
      };
    } finally {
      setUploading(false);
    }
  }, []);

  /**
   * Get file view URL
   * @param {string} fileId - The file ID
   * @returns {Promise<Object>} File URL object
   */
  const getFileView = useCallback(async (fileId) => {
    setLoading(true);
    setError(null);

    try {
      if (!fileId) {
        throw new Error("No file ID provided");
      }

      const response = storage.getFileView(BUCKET_ID, fileId);
      
      return {
        success: true,
        fileUrl: response.href,
        response,
      };
    } catch (err) {
      console.error("Error getting file view:", err);
      setError(err.message || "Failed to get file view");
      return {
        success: false,
        error: err.message || "Failed to get file view",
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get file preview URL with dimensions
   * @param {string} fileId - The file ID
   * @param {number} [width=400] - Preview width
   * @param {number} [height=400] - Preview height
   * @param {string} [gravity="center"] - Crop gravity
   * @param {number} [quality=100] - Image quality (0-100)
   * @param {string} [output] - Output format (jpg, jpeg, png, gif, webp)
   * @returns {Promise<Object>} Preview URL object
   */
  const getFilePreview = useCallback(
    async (
      fileId,
      width = 100,
      height = 100,
      gravity = "center",
      quality = 100,
      output = undefined
    ) => {
      setLoading(true);
      setError(null);

      try {
        if (!fileId) {
          throw new Error("No file ID provided");
        }

        const response = storage.getFilePreview(
          BUCKET_ID,
          fileId,
          width,
          height,
          gravity,
          quality,
          0, // borderWidth
          "", // borderColor
          0, // borderRadius
          1, // opacity
          0, // rotation
          "", // background
          output
        );

        return {
          success: true,
          previewUrl: response.href,
          response,
        };
      } catch (err) {
        console.error("Error getting file preview:", err);
        setError(err.message || "Failed to get file preview");
        return {
          success: false,
          error: err.message || "Failed to get file preview",
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Download a file
   * @param {string} fileId - The file ID
   * @returns {Promise<Object>} Download URL object
   */
  const downloadFile = useCallback(async (fileId) => {
    setLoading(true);
    setError(null);

    try {
      if (!fileId) {
        throw new Error("No file ID provided");
      }

      const response = storage.getFileDownload(BUCKET_ID, fileId);

      return {
        success: true,
        downloadUrl: response.href,
        response,
      };
    } catch (err) {
      console.error("Error downloading file:", err);
      setError(err.message || "Failed to download file");
      return {
        success: false,
        error: err.message || "Failed to download file",
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a file from storage
   * @param {string} fileId - The file ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  const deleteFile = useCallback(async (fileId) => {
    setDeleting(true);
    setError(null);

    try {
      if (!fileId) {
        throw new Error("No file ID provided");
      }

      const response = await storage.deleteFile(BUCKET_ID, fileId);

      return {
        success: true,
        message: "File deleted successfully",
        response,
      };
    } catch (err) {
      console.error("Error deleting file:", err);
      setError(err.message || "Failed to delete file");
      return {
        success: false,
        error: err.message || "Failed to delete file",
      };
    } finally {
      setDeleting(false);
    }
  }, []);

  /**
   * Get file metadata
   * @param {string} fileId - The file ID
   * @returns {Promise<Object>} File metadata
   */
  const getFileMetadata = useCallback(async (fileId) => {
    setLoading(true);
    setError(null);

    try {
      if (!fileId) {
        throw new Error("No file ID provided");
      }

      const response = await storage.getFile(BUCKET_ID, fileId);

      return {
        success: true,
        metadata: {
          id: response.$id,
          name: response.name,
          size: response.sizeOriginal,
          mimeType: response.mimeType,
          createdAt: response.$createdAt,
          updatedAt: response.$updatedAt,
        },
        response,
      };
    } catch (err) {
      console.error("Error getting file metadata:", err);
      setError(err.message || "Failed to get file metadata");
      return {
        success: false,
        error: err.message || "Failed to get file metadata",
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Upload image with validation
   * @param {File} file - The image file to upload
   * @param {Object} options - Upload options
  * @param {number} [options.maxSize=512000] - Max file size in bytes (default: 500KB)
   * @param {string[]} [options.allowedTypes] - Allowed MIME types
   * @returns {Promise<Object>} Upload result
   */
  const uploadImage = useCallback(
    async (file, options = {}) => {
      const {
        maxSize = 500 * 1024, // 500KB default
        allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"],
      } = options;

      setError(null);

      try {
        // Validate file type
        if (!file.type.startsWith("image/")) {
          throw new Error("Please select an image file");
        }

        // Validate specific image types
        if (allowedTypes && !allowedTypes.includes(file.type)) {
          throw new Error(
            `Invalid image type. Allowed types: ${allowedTypes.join(", ")}`
          );
        }

        // Validate file size
        if (file.size > maxSize) {
          const maxSizeLabel = maxSize < 1024 * 1024
            ? `${Math.round(maxSize / 1024)}KB`
            : `${(maxSize / (1024 * 1024)).toFixed(2)}MB`;
          throw new Error(`Image size should be less than ${maxSizeLabel}`);
        }

        // Upload the file
        return await uploadFile(file);
      } catch (err) {
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [uploadFile]
  );

  /**
   * Create image preview from file
   * @param {File} file - The file to preview
   * @returns {Promise<string>} Base64 data URL
   */
  const createPreview = useCallback((file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("No file provided"));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset progress
   */
  const resetProgress = useCallback(() => {
    setProgress(0);
  }, []);

  return {
    // State
    uploading,
    deleting,
    loading,
    error,
    progress,

    // Methods
    uploadFile,
    uploadImage,
    getFileView,
    getFilePreview,
    downloadFile,
    deleteFile,
    getFileMetadata,
    createPreview,
    clearError,
    resetProgress,
  };
};

export default useFile;